import * as React from 'react';
import withStyles from 'react-jss';
import { StylesProps, KeyLabel, KeyLabelObject } from 'src/Library/Types';
import {
  Row,
  Form,
  Col,
  Select,
  Icon,
  Modal,
  Button,
  AutoComplete,
  DatePicker,
  Checkbox,
} from 'src/Library/AntdLibComponents';
import { useGenericState } from 'src/Library/UseGenericState';
import {
  renderSelectDropDown, renderLabelAndInput, renderLabelAndTextArea, renderLabelAndInputNumber,
} from 'src/Library/Common';
import { connect } from 'react-redux';
import { isValidArray, getDateValue, isValidNumber } from 'src/Library/Helper';
import { ShipmentData } from '../../../SE.Types';
import { FileVGMModalStyles } from './FileVGMModal.styles';
import moment from 'moment';
import { renderWeightOrVolumeUnit } from './UploadSIModal.utils';
import Asterisk from 'src/components/common/Asterisk';

interface IProps extends StylesProps<ReturnType<typeof FileVGMModalStyles>> {
  form;
  weighmentDetails;
  containerKey: number;
  weightUnits: string[];
  handleRemoveContainer: (containerKey) => void;
  containerTypes: KeyLabel[];
  shipmentData: ShipmentData;
  containerNoList;
  isFetchingContainers;
  getContainers;
  containerSizes: string[];
  cargoTypes: KeyLabel [];
  isWeighbridgeDetailsAutofilled: boolean;
  productIMOClassCodeMap: KeyLabelObject;
  isLoading?: boolean;
  imoClassCodes: KeyLabel [];
  cargoToContainerListMap;
  draftData;
}

interface StateProps {
  isRemoveContainerModalVisible?: boolean;
}

const FileVGMContainerAndWeighbridge = (props: IProps): React.ReactElement => {

  const {
    classes,
    form,
    weighmentDetails,
    containerKey,
    weightUnits,
    handleRemoveContainer,
    containerTypes,
    containerNoList,
    isFetchingContainers,
    getContainers,
    containerSizes,
    cargoTypes,
    isWeighbridgeDetailsAutofilled,
    shipmentData,
    productIMOClassCodeMap,
    isLoading,
    imoClassCodes,
    cargoToContainerListMap,
    draftData,
  } = props;

  const [state, setState] = useGenericState<StateProps>({

  });

  const {
    isRemoveContainerModalVisible,
  } = state;

  const {
    getFieldDecorator,
    getFieldValue,
    setFieldsValue,
  } = form;

  const labelInputProps = {
    colSpan: {
      left: 7,
      right: 12,
    },
    form,
    classes,
    precision: 2,
    min: 0,
    disabled: isLoading,
  };
  const getContainerNumbers = () => {
    const queryString = getFieldValue(`weighmentDetails[${containerKey}].containerNumber`);
    const weighmentDetailsList = getFieldValue('weighmentDetails');
    const filledContainerNumbers = isValidArray(weighmentDetailsList) ?
      weighmentDetailsList?.map(item => item.containerNumber) : [];

    if (!queryString) {
      return containerNoList?.filter(
        item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1);
    }

    const filteredContainers = containerNoList?.filter(
      item => filledContainerNumbers?.indexOf(item?.containerNumber) === -1 &&
      item?.containerNumber?.toLowerCase()?.includes(queryString?.toLowerCase()));
    return filteredContainers;
  };

  const handleContainerNumberSelection = (options, containerKey) => {
    setFieldsValue({
      [`weighmentDetails[${containerKey}].containerSize`]: options?.props?.containerSize,
      [`weighmentDetails[${containerKey}].containerType`]: options?.props?.containerType,
      [`weighmentDetails[${containerKey}].cargoType`]: undefined,
      [`weighmentDetails[${containerKey}].unoNo`]: undefined,
      [`weighmentDetails[${containerKey}].imoNo`]: productIMOClassCodeMap[shipmentData?.cargoSubtype],
    });
  }

  const renderContainerNumber = () : React.ReactElement => {
    const containerNumbers = getContainerNumbers()
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`weighmentDetails[${containerKey}].containerNumber`, {
          rules: [
            { required: true, message: 'Required' },
            { max: 11, message: 'Must have 11 characters' },
            { min: 11, message: 'Must have 11 characters' },
            { pattern: /^[a-zA-Z]{4}[0-9]{7}$/, message: 'Must contain 4 alphabets followed by 7 numbers' },
          ],
          initialValue: weighmentDetails?.[containerKey]?.containerNumber,
        })(
          <AutoComplete
            style={{ fontSize: '12px' }}
            placeholder="Enter Container No"
            onFocus={() => getContainers()}
            disabled={isLoading}
            dropdownRender={menu => renderSelectDropDown(menu, isFetchingContainers)}
            onSelect={(val, options) => handleContainerNumberSelection(options, containerKey)}
          >
            {containerNumbers?.map((container) => (
              <AutoComplete.Option
                key={container?.containerNumber}
                { ...container }
                value={container?.containerNumber}
              >
                {container?.containerNumber}
              </AutoComplete.Option>
            ))}
          </AutoComplete>
        )}
      </Form.Item>
    );
  };

  const renderContainerAndCargo = (key, placeholder, arrayList, initialValue?, resetField?) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(key, {
          initialValue,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            disabled={isLoading}
            optionFilterProp="children"
            placeholder={placeholder}
            dropdownMatchSelectWidth={false}
            onChange={() => {
              if (resetField) {
                setFieldsValue({
                  [`weighmentDetails[${containerKey}].containerType`]: undefined
                })
              }
            }}
          >
            {arrayList?.map(item =>
              <Select.Option key={item.key}>{item.label}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const handleCloseConfirmation = () : void => {
    setState({ isRemoveContainerModalVisible: false });
  };

  const renderRemoveModalConfirmation = () : React.ReactElement => {
    return (
      <Modal
        title={
          <span><Icon type="exclamation-circle" theme="filled" style={{
            fontSize: '16px',
            color: '#FAB826',
            marginRight: '12px',
          }}/>Confirm Action</span>
        }
        width={400}
        visible={isRemoveContainerModalVisible}
        footer={
          <React.Fragment>
            <span style={{
              cursor: 'pointer',
              fontSize: '12px',
              marginRight: '16px',
            }} onClick={handleCloseConfirmation}>Cancel</span>
            <Button
              onClick={() => {
                handleRemoveContainer(containerKey);
                handleCloseConfirmation();
              }}
              className={classes.addContainerButton}
            >
              Submit
            </Button>
          </React.Fragment>
        }
        onCancel={handleCloseConfirmation}
      >
        <div style={{ fontSize: '16px', marginBottom: '25px' }}>
          This will remove all the
          <strong> {`Container ${containerKey + 1} and Weighment ${containerKey + 1}`} </strong>
          details. Are you sure you want to proceed?
        </div>
      </Modal>
    );
  };

  const renderContainerSize = (containerKey) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`weighmentDetails[${containerKey}].containerSize`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue: weighmentDetails?.[containerKey]?.containerSize,
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            disabled={isLoading}
            optionFilterProp="children"
            placeholder="Select Container Size"
          >
            {containerSizes?.map(item =>
              <Select.Option key={item}>{item}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const renderContainerDetails = () : React.ReactElement => {
    const selectedCargoType = getFieldValue(`weighmentDetails[${containerKey}].cargoType`);
    const cargoWiseContainerList = selectedCargoType ? containerTypes?.filter(item => (
      cargoToContainerListMap?.[selectedCargoType]?.includes(item.key)
    )) : containerTypes;
    return (
      <Row className={classes.containerDiv}>
        <Col span={23}>
          <div className={classes.contentHeading}>Container {containerKey + 1}</div>              
          <Row style={{ paddingTop: '8px' }}>
            <Col span={3} className={classes.label}>
              Container No<Asterisk />
            </Col>
            <Col span={4}>
              {renderContainerNumber()}
            </Col>
            <Col span={2} />
          </Row>
          <Row style={{ paddingTop: '8px' }}>
            <Col span={7}>
              <Col span={10} className={classes.label}>
                Container Size<Asterisk />
              </Col>
              <Col span={14}>
                {renderContainerSize(containerKey)}
              </Col>
            </Col>
            <Col span={1} />
            <Col span={8}>
              <Col span={8} className={classes.label}>
                Container Type
              </Col>
              <Col span={16}>
                {renderContainerAndCargo(
                  `weighmentDetails[${containerKey}].containerType`,
                  'Select Container Type',
                  cargoWiseContainerList,
                  weighmentDetails?.[containerKey]?.containerType,
                )}
              </Col>
            </Col>
            <Col span={1} />
            <Col span={7}>
              <Col span={8} className={classes.label}>
                Cargo Type
              </Col>
              <Col span={16}>
                {renderContainerAndCargo(
                  `weighmentDetails[${containerKey}].cargoType`,
                  'Select Cargo Type',
                  cargoTypes,
                  weighmentDetails?.[containerKey]?.cargoType || shipmentData?.cargoType,
                  true,
                )}
              </Col>
            </Col>
          </Row>
          {getFieldValue(`weighmentDetails[${containerKey}].cargoType`) === 'HAZARDOUS' &&
            <Row style={{ paddingTop: '8px' }}>
              <Col span={7}>
                <Col span={10} className={classes.label}>
                  IMO No.<Asterisk />
                </Col>
                <Col span={14}>
                  {renderImoNo(containerKey)}
                </Col>
              </Col>
              <Col span={1} />
              <Col span={8}>
                {renderLabelAndInputNumber({
                  ...labelInputProps,
                  colSpan: {
                    left: 8,
                    right: 12,
                  },
                  precision: 0,
                  label: <>UNO No.<Asterisk /></>,
                  min: 1,
                  key: `weighmentDetails[${containerKey}].unoNo`,
                  initialValue: weighmentDetails?.[containerKey]?.unoNo,
                  placeholder: 'Enter UNO No.',
                  rules: [
                    {
                      validator: (rule, value, cb) => {
                        if (!isValidNumber(value)) {
                          cb('Required');
                        } else if (value <= 0) {
                          cb('Minimum 1 allowed');
                        } else if (value > 3600) {
                          cb('Maximum 3600 allowed');
                        } else cb();
                      },
                    },
                  ],
                })}
              </Col>
            </Row>
          }
        </Col>
        <Col span={1}>
          {weighmentDetails?.length > 1 &&
            <Icon
              type="delete"
              style={{ fontSize: '16px' }}
              onClick={() => setState({ isRemoveContainerModalVisible: true })}
            />
          }
        </Col>
      </Row>
    )
  };


  const renderImoNo = (containerKey) : React.ReactElement => {
    return (
      <Form.Item style={{ marginBottom: '12px' }}>
        {getFieldDecorator(`weighmentDetails[${containerKey}].imoNo`, {
          rules: [{ required: true, message: 'Required' }],
          initialValue: weighmentDetails?.[containerKey]?.imoNo ||
          productIMOClassCodeMap[shipmentData?.cargoSubtype],
        })(
          <Select
            getPopupContainer={(trigger: any) => trigger.parentNode}
            className={classes.selectField}
            showSearch
            allowClear
            disabled={isLoading}
            optionFilterProp="children"
            placeholder="Select IMO No"
            dropdownMatchSelectWidth={false}
          >
            {imoClassCodes?.map(item =>
              <Select.Option key={item.key}>{item.label}</Select.Option>
            )}
          </Select>
        )}
      </Form.Item>
    );
  };

  const setCommonWeighmentDetails = (props?) => {
    const enteredRegNo = props?.weighBridgeRegistrationNumber;
    const enteredAddress = props?.weighBridgeAddress;
    const weighmentDetailsAdded = getFieldValue('weighmentDetails')
    const weighments = weighmentDetailsAdded?.map(item => ({
      ...item,
      weighBridgeRegistrationNumber: enteredRegNo || weighmentDetailsAdded?.[0]?.weighBridgeRegistrationNumber,
      weighBridgeAddress: enteredAddress || weighmentDetailsAdded?.[0]?.weighBridgeAddress,
    }));
    setFieldsValue({ weighmentDetails: weighments });
  }

  const renderKeepCommonWeighbridgeCheckbox = () : React.ReactElement => {
    return (
      <div style={{ margin: '12px 0px 20px' }}>
        <Form.Item style={{ marginBottom: 0 }}>
          {getFieldDecorator('keepCommonWeighbridgeDetails', {
            initialValue: draftData?.keepCommonWeighbridgeDetails || false,
            valuePropName: 'checked',
          })(
            <Checkbox
              onChange={(e) => {
                if (e.target.checked) {
                  setCommonWeighmentDetails();
                }
              }}
              disabled={isLoading}
            >
              Keep common weighbridge details
            </Checkbox>
          )}
        </Form.Item>
        <div className={classes.autofillInfo} style={{ margin: '-10px 0 0 22px' }}>
          This will copy the same Weighbridge Reg No.
          and Address across the entire list from the first set of details that you fill.
        </div>
      </div>
    )
  }

  const renderWeighmentDetails = () : React.ReactElement => {
    const isKeepCommonWeighbridgeDetails = 
      getFieldValue('keepCommonWeighbridgeDetails') && containerKey !== 0;
    return (
      <Row className={classes.containerDiv} style={{ borderBottom: 'unset' }}>
        <Col span={23}>
          <div className={classes.contentHeading}>Weighment Details {containerKey + 1}</div>              
          <Row style={{ paddingTop: '8px' }}>
            <Col span={9}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Weighbridge Reg. No.<Asterisk /></>,
                key: `weighmentDetails[${containerKey}].weighBridgeRegistrationNumber`,
                initialValue: isKeepCommonWeighbridgeDetails ?
                  getFieldValue(`weighmentDetails[0].weighBridgeRegistrationNumber`)
                : weighmentDetails?.[containerKey]?.weighBridgeRegistrationNumber,
                placeholder: 'Enter Reg. No.',
                rules: [
                  { required: !isWeighbridgeDetailsAutofilled, message: 'Required' },
                  { max: 100, message: 'Maximum 100 characters allowed' }
                ],
                disabled: isWeighbridgeDetailsAutofilled || isLoading || isKeepCommonWeighbridgeDetails,
                onChange: (e) => {
                  if (getFieldValue('keepCommonWeighbridgeDetails') && containerKey === 0) {
                    setCommonWeighmentDetails({ weighBridgeRegistrationNumber: e.target.value });
                  }
                },
              })}
            </Col>
            <Col span={2} />
            <Col span={12}>
              {renderLabelAndTextArea({
                ...labelInputProps,
                colSpan: {
                  left: 5,
                  right: 16,
                },
                label: <>Address<Asterisk /></>,
                key: `weighmentDetails[${containerKey}].weighBridgeAddress`,
                initialValue: isKeepCommonWeighbridgeDetails ?
                  getFieldValue(`weighmentDetails[0].weighBridgeAddress`)
                : weighmentDetails?.[containerKey].weighBridgeAddress,
                placeholder: 'Enter Address',
                rules: [
                  { required: !isWeighbridgeDetailsAutofilled, message: 'Required' },
                  { max: 107, message: 'Maximum 107 characters allowed'},
                ],
                disabled: isWeighbridgeDetailsAutofilled || isLoading || isKeepCommonWeighbridgeDetails,
                onChange: (e) => {
                  if (getFieldValue('keepCommonWeighbridgeDetails') && containerKey === 0) {
                    setCommonWeighmentDetails({ weighBridgeAddress: e.target.value });
                  }
                },
              })}
            </Col>
          </Row>
          {containerKey === 0 && renderKeepCommonWeighbridgeCheckbox()}
          <Row style={{ paddingTop: '8px' }}>
            <Col span={9}>
              {renderLabelAndInput({
                ...labelInputProps,
                label: <>Weighbridge Slip No.<Asterisk /></>,
                key: `weighmentDetails[${containerKey}].weighBridgeSlipNumber`,
                initialValue: weighmentDetails?.[containerKey]?.weighBridgeSlipNumber,
                placeholder: 'Enter Slip No.',
                rules: [
                  { required: !isWeighbridgeDetailsAutofilled, message: 'Required' },
                  { max: 100, message: 'Maximum 100 characters allowed' }
                ],
                disabled: isWeighbridgeDetailsAutofilled || isLoading,
              })}
            </Col>
            <Col span={2} />
            <Col span={12}>
              <Col span={5} className={classes.label}>
                Timestamp of Weighing<Asterisk />
              </Col>
              <Col span={12}>
                <Form.Item>
                  {getFieldDecorator(`weighmentDetails[${containerKey}].weighingTimestamp`, {
                    rules: [{ required: !isWeighbridgeDetailsAutofilled, message: 'Required' }],
                    initialValue: getDateValue(weighmentDetails?.[containerKey]?.weighingTimestamp),
                  })(
                    <DatePicker
                      showTime={{
                        defaultValue: moment('23:59', 'HH:mm'),
                      }}
                      className={classes.datePicker}
                      disabled={isWeighbridgeDetailsAutofilled || isLoading}
                      format="DD MMM YYYY | HH:mm"
                      placeholder="Select Date &amp; Time of Weighing"
                    />
                  )}
                </Form.Item>
              </Col>
            </Col>
          </Row>
          <Row style={{ paddingTop: '8px' }}>
            <Col span={9}>
              <div>
                {renderLabelAndInputNumber({
                  ...labelInputProps,
                  colSpan: {
                    left: 7,
                    right: 7,
                  },
                  label: <>VGM<Asterisk /></>,
                  key: `weighmentDetails[${containerKey}].weight.value`,
                  initialValue: weighmentDetails?.[containerKey]?.weight?.value,
                  placeholder: '0.00',
                  rules: [{ required: !isWeighbridgeDetailsAutofilled, message: 'Required' }],
                  disabled: isWeighbridgeDetailsAutofilled || isLoading,
                })}
                <Col span={5} style={{ paddingLeft: '6px' }}>
                  {renderWeightOrVolumeUnit(
                    form,
                    classes,
                    `weighmentDetails[${containerKey}].weight.unit`,
                    weightUnits,
                    weighmentDetails?.[containerKey]?.weight?.unit || 'kg',
                    isWeighbridgeDetailsAutofilled || isLoading,
                  )}
                </Col>
              </div>
            </Col>
            <Col span={2} />
            <Col span={12}>
              <div>
                {renderLabelAndInputNumber({
                  ...labelInputProps,
                  colSpan: {
                    left: 5,
                    right: 5,
                  },
                  label: <>CSC Plate Max. Wt. Limit<Asterisk /></>,
                  key: `weighmentDetails[${containerKey}].cscPlateMaxWeightLimit.value`,
                  initialValue: weighmentDetails?.[containerKey]?.cscPlateMaxWeightLimit?.value,
                  placeholder: '0.00',
                  rules: [{ required: !isWeighbridgeDetailsAutofilled, message: 'Required' }],
                  disabled: isWeighbridgeDetailsAutofilled || isLoading,
                })}
                <Col span={4} style={{ paddingLeft: '6px' }}>
                  {renderWeightOrVolumeUnit(
                    form,
                    classes,
                    `weighmentDetails[${containerKey}].cscPlateMaxWeightLimit.unit`,
                    weightUnits,
                    weighmentDetails?.[containerKey]?.cscPlateMaxWeightLimit?.unit || 'kg',
                    isWeighbridgeDetailsAutofilled || isLoading,
                  )}
                </Col>
              </div>
            </Col>
          </Row>
        </Col>
      </Row>
    )
  };

  const renderContainerAndWeighmentDetails = () : React.ReactElement => {
    return (
      <React.Fragment>
        {renderContainerDetails()}
        {renderWeighmentDetails()}
      </React.Fragment>
    );
  };

  return (
    <React.Fragment>
      {renderContainerAndWeighmentDetails()}
      {isRemoveContainerModalVisible && renderRemoveModalConfirmation()}
    </React.Fragment>
  );
};

const mapStateToProps = ({ masterData }, ownProps) => ({
  containerTypes: masterData.odexMasterData?.containerTypes,
  containerSizes: masterData.odexMasterData?.containerSize,
  cargoTypes: masterData.odexMasterData?.cargoTypes,
  weightUnits: masterData.odexMasterData?.weightUnits,
  productIMOClassCodeMap: masterData.odexMasterData?.productIMOClassCodeMap,
  imoClassCodes: masterData.odexMasterData?.imoClassCodes,
  cargoToContainerListMap: masterData.execution?.cargoToContainerListMap,
});

export default connect(
  mapStateToProps,
  null,
  )(React.memo(withStyles(FileVGMModalStyles)(FileVGMContainerAndWeighbridge)));